// The account's language on Firebase (SPEC-31). The owner reads her
// settings document straight from Firestore, as the hub reads her profile
// (firestore.rules: an account reads its own subtree); a change goes
// through the saveSettings callable in europe-west1, which writes it.
//
// Every module comes through loadFirebase: without a network it refuses
// before requesting a chunk, so a tap offline costs the save only, and the
// provider tries again when the network is back.

import { FUNCTIONS_REGION } from "../data/transport";
import { isLanguage } from "../i18n/language";
import { loadFirebase } from "../lib/firebaseModules";
import type { AccountSettings, SaveSettingsRequest, SettingsResponse } from "../schema/types";
import type { AccountLanguage } from "./settings";

export const firebaseSettings: AccountLanguage = {
  async accountId() {
    const [{ auth }] = await loadFirebase();
    await auth.authStateReady();
    return auth.currentUser?.uid ?? null;
  },

  async read(accountId) {
    const [{ db }, , , firestore] = await loadFirebase();
    const snapshot = await firestore.getDoc(firestore.doc(db, "accounts", accountId, "settings", "current"));
    if (!snapshot.exists()) return null;
    const { language } = snapshot.data() as Partial<AccountSettings>;
    if (isLanguage(language)) return language;
    // A language a later version added: the device keeps its own, and nothing is saved over hers.
    const unknown = new Error("the account's language is not one this version knows");
    unknown.name = "UnknownLanguage";
    throw unknown;
  },

  async save(language) {
    const [{ app }, , functions] = await loadFirebase();
    const saveSettings = functions.httpsCallable<SaveSettingsRequest, SettingsResponse>(
      functions.getFunctions(app, FUNCTIONS_REGION),
      "saveSettings",
    );
    await saveSettings({ language });
  },
};
